// AdminSettingsEnvPage — centralises external API keys & IDs the admin
// manages (Apollo, GA4, Search Console, ContentStudio, Resend, Stripe, …).
// Edits are persisted in Mongo and hot-swapped into os.environ so the
// backend picks up the new value without a Railway restart.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Settings, Save, Eye, EyeOff, Trash2, Zap, CheckCircle2, AlertTriangle,
  Loader2, ExternalLink, RefreshCw, Lock,
} from "lucide-react";
import { API, authHeaders } from "@/lib/adminApi";

// Thin wrapper — adminApi doesn't yet expose settings-env endpoints and we
// need multipart-free JSON here, so a local helper is enough.
const fetchJson = async (path, opts = {}) => {
  const r = await fetch(`${API}/api${path}`, {
    method: opts.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `${r.status} ${r.statusText}`);
  return data;
};

const SOURCE_BADGE = {
  mongo: { bg: "bg-emerald-50", fg: "text-emerald-700", label: "UI" },
  env:   { bg: "bg-indigo-50",  fg: "text-indigo-700",  label: "ENV" },
  unset: { bg: "bg-stone-100",  fg: "text-stone-500",   label: "—"   },
};

const SettingRow = ({ item, onSave, onDelete, onTest, revealedKeys, toggleReveal }) => {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const revealed = revealedKeys[item.key];
  const source = SOURCE_BADGE[item.source] || SOURCE_BADGE.unset;

  const effectiveValue = revealed && revealed.value !== undefined ? revealed.value : item.value;
  const showEditor = editing || !item.is_set;

  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onSave(item.key, draft);
      setDraft("");
      setEditing(false);
    } finally { setSaving(false); }
  };
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await onTest(item.key);
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, detail: e?.message || String(e) });
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid={`setting-row-${item.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-[14px] font-semibold">{item.label}</span>
            <span className="font-mono text-[10.5px] text-stone-500">{item.key}</span>
            {item.is_secret && <Lock size={10} className="text-amber-600" />}
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] ${source.bg} ${source.fg}`}>
              {source.label}
            </span>
          </div>
          {item.hint && <div className="text-[11.5px] text-stone-500 mt-1">{item.hint}</div>}
        </div>
        {item.doc_url && (
          <a href={item.doc_url} target="_blank" rel="noreferrer" className="text-[11px] text-[color:var(--gv-pink)] inline-flex items-center gap-1 shrink-0">
            Doc <ExternalLink size={10} />
          </a>
        )}
      </div>

      {!showEditor && (
        <div className="flex items-center gap-2 mt-2">
          <div className="flex-1 font-mono text-[12.5px] bg-stone-50 px-3 py-2 rounded-[4px] border border-stone-200 truncate" title={effectiveValue}>
            {effectiveValue || <span className="text-stone-400 italic">non défini</span>}
          </div>
          {item.is_secret && item.is_set && (
            <button
              onClick={() => toggleReveal(item.key)}
              className="p-2 rounded-[4px] border border-stone-200 bg-white hover:bg-stone-50"
              data-testid={`reveal-${item.key}`}
              title={revealed ? "Masquer" : "Révéler"}
            >
              {revealed ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          )}
          <button
            onClick={() => { setDraft(""); setEditing(true); }}
            className="px-3 py-2 rounded-[4px] border border-stone-200 bg-white hover:bg-stone-50 text-[12.5px] font-medium"
            data-testid={`edit-${item.key}`}
          >
            Modifier
          </button>
          {item.is_set && (
            <>
              <button
                onClick={handleTest}
                disabled={testing}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-[4px] border border-stone-200 bg-white text-[12.5px] font-medium hover:bg-stone-50 disabled:opacity-50"
                data-testid={`test-${item.key}`}
              >
                {testing ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Tester
              </button>
              {item.source === "mongo" && (
                <button
                  onClick={() => onDelete(item.key)}
                  className="p-2 rounded-[4px] border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                  data-testid={`delete-${item.key}`}
                  title="Supprimer (retombe sur la valeur Railway si définie)"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {showEditor && (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={item.hint || "Nouvelle valeur…"}
            rows={item.key === "GOOGLE_SERVICE_ACCOUNT_JSON_B64" ? 4 : 1}
            className="w-full px-3 py-2 rounded-[4px] border border-stone-300 text-[12.5px] font-mono resize-y"
            data-testid={`input-${item.key}`}
            spellCheck={false}
            autoFocus
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleSave}
              disabled={!draft.trim() || saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[12.5px] font-medium disabled:opacity-50"
              data-testid={`save-${item.key}`}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Enregistrer
            </button>
            {item.is_set && (
              <button
                onClick={() => { setEditing(false); setDraft(""); }}
                className="px-3 py-2 rounded-[4px] border border-stone-200 bg-white text-[12.5px]"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      )}

      {testResult && (
        <div
          className={`mt-2 text-[11.5px] rounded-[4px] px-3 py-2 inline-flex items-start gap-1.5 ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
          data-testid={`test-result-${item.key}`}
        >
          {testResult.ok ? <CheckCircle2 size={11} className="mt-0.5" /> : <AlertTriangle size={11} className="mt-0.5" />}
          <div>
            <div className="font-semibold">{testResult.ok ? "Connexion OK" : "Échec du test"}</div>
            <div className="opacity-70 mt-0.5 break-all">HTTP {testResult.status} — {testResult.detail}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminSettingsEnvPage = () => {
  const [data, setData] = useState({ items: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchJson("/admin/settings-env");
      setData(d);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleReveal = async (key) => {
    if (revealedKeys[key]) {
      setRevealedKeys((p) => ({ ...p, [key]: undefined }));
      return;
    }
    try {
      const d = await fetchJson(`/admin/settings-env?reveal=true`);
      const found = (d.items || []).find((it) => it.key === key);
      if (found) setRevealedKeys((p) => ({ ...p, [key]: { value: found.value } }));
    } catch (e) {
      setError(e?.message || String(e));
    }
  };

  const handleSave = async (key, value) => {
    await fetchJson(`/admin/settings-env/${key}`, { method: "PUT", body: { value } });
    await load();
  };
  const handleDelete = async (key) => {
    if (!window.confirm(`Supprimer la valeur UI de ${key} ? (le backend retombera sur la valeur Railway si elle existe)`)) return;
    await fetchJson(`/admin/settings-env/${key}`, { method: "DELETE" });
    setRevealedKeys((p) => ({ ...p, [key]: undefined }));
    await load();
  };
  const handleTest = async (key) => fetchJson(`/admin/settings-env/${key}/test`, { method: "POST" });

  const itemsByCategory = useMemo(() => {
    const map = {};
    (data.items || []).forEach((it) => {
      (map[it.category] = map[it.category] || []).push(it);
    });
    return map;
  }, [data.items]);

  return (
    <div data-testid="admin-settings-env">
      <header className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] inline-flex items-center gap-2">
            <Settings size={24} /> Variables & clés API
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Gère toutes les clés externes depuis cette page — les modifications sont appliquées à chaud, sans redémarrer Railway.
          </p>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[4px] border border-stone-200 bg-white text-[13px] disabled:opacity-50" data-testid="refresh-settings">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Rafraîchir
        </button>
      </header>

      <div className="bg-amber-50 border border-amber-200 rounded-[6px] p-3 mb-5 text-[12.5px] text-amber-900 flex items-start gap-2">
        <Lock size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Sécurité</strong> — les secrets (<Lock size={10} className="inline" />) sont masqués par défaut. Clique sur l'œil pour les révéler.
          Les clés critiques d'infrastructure (MongoDB, JWT, mot de passe admin) ne sont volontairement pas exposées ici.
          Badge <strong>UI</strong> = valeur poussée depuis cette page · <strong>ENV</strong> = valeur Railway/.env en fallback.
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-[5px] p-3 text-sm text-rose-700 mb-4" data-testid="settings-error">
          {error}
        </div>
      )}

      {(data.categories || []).map((cat) => {
        const items = itemsByCategory[cat.id] || [];
        if (items.length === 0) return null;
        return (
          <section key={cat.id} className="mb-6" data-testid={`category-${cat.id}`}>
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="font-display text-[18px] font-semibold">{cat.label}</h2>
              <span className="text-[11.5px] text-stone-500">{cat.description}</span>
            </div>
            <div className="space-y-2">
              {items.map((it) => (
                <SettingRow
                  key={it.key}
                  item={it}
                  onSave={handleSave}
                  onDelete={handleDelete}
                  onTest={handleTest}
                  revealedKeys={revealedKeys}
                  toggleReveal={toggleReveal}
                />
              ))}
            </div>
          </section>
        );
      })}

      {loading && (
        <div className="text-center text-stone-500 text-sm py-8"><Loader2 size={16} className="animate-spin inline mr-2" /> Chargement…</div>
      )}
    </div>
  );
};

export default AdminSettingsEnvPage;
